"use client";

import { Button } from "@ui/components/core/Button";
import { ModalVideoPlayer } from "@ui/components/core/ModalVideoPlayer";
import { clsx } from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useId, useState } from "react";
import type { QuoteCarouselBlock } from "@mw/shared/payload-types";
import { getImageProps } from "@utilities/payload";
import Image from "next/image";
import { pxToRem } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { sizes } from "@ui/lib/sizes";
import { AnimatePresence } from "motion/react";
import * as m from "motion/react-m";
import { HairlineCardBorder } from "@ui/components/core/HairlineCardBorder";

type QuoteCarouselQuote = NonNullable<QuoteCarouselBlock["quotes"]>[number];

function getQuoteVideoUrl(quote: QuoteCarouselQuote) {
  if (quote.asset.template !== "video") {
    return null;
  }

  return quote.asset.video?.video?.url?.trim() || null;
}

function QuoteAsset({
  quote,
  logoStyle,
  active,
}: {
  quote: QuoteCarouselQuote;
  logoStyle: React.CSSProperties;
  active: boolean;
}) {
  if (!quote) {
    return null;
  }

  const videoUrl = getQuoteVideoUrl(quote);
  const imageProps =
    quote.asset.template === "video"
      ? getImageProps(quote.asset.video?.thumbnail, { placeholder: true, fill: true })
      : null;
  const logoProps = quote.asset.template === "logo" ? getImageProps(quote.asset.logo?.logo) : null;

  return (
    <div
      className={clsx(
        "tw-absolute tw-inset-0",
        !active && "tw-opacity-0",
        logoProps && "tw-flex tw-items-center tw-justify-center tw-p-6",
      )}
    >
      {imageProps && videoUrl ? (
        <>
          <Image
            {...imageProps}
            sizes={sizes("100vw", { sm: "33vw", xl: pxToRem(370) })}
            alt=''
            className='tw-object-cover'
          />
          <div className='tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center'>
            <Button asChild size='pill-xl' variant='elevated'>
              <div>
                <svg viewBox='0 0 24 24' className='tw-size-5'>
                  <path
                    fill='currentColor'
                    d='M9.244 2.368C7.414 1.184 5 2.497 5 4.676v14.648c0 2.18 2.414 3.493 4.244 2.309l11.318-7.324c1.675-1.084 1.675-3.534 0-4.618z'
                  />
                </svg>
              </div>
            </Button>
          </div>
        </>
      ) : logoProps ? (
        <div className='tw-absolute tw-inset-0 tw-bg-cream tw-flex tw-items-center tw-justify-center'>
          <div className='tw-relative tw-w-full' style={logoStyle}>
            <Image {...logoProps} alt='' className='tw-absolute tw-inset-0 tw-size-full tw-object-contain' />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function QuoteVideo({ quote }: { quote: QuoteCarouselQuote }) {
  const t = useTranslations("ClientKeys.Globals");

  const videoUrl = getQuoteVideoUrl(quote);

  if (!videoUrl) {
    return null;
  }

  return (
    <ModalVideoPlayer
      video={videoUrl}
      customTrigger={
        <button
          type='button'
          className='tw-absolute tw-inset-0 tw-overflow-hidden tw-rounded-2.5xl tw-outline-none focus-visible:tw-outline-focus'
          aria-label={t("play")}
        />
      }
    />
  );
}

export function BlockQuoteCarousel({
  quotes,
  label,
  logoStyle,
}: QuoteCarouselBlock & { logoStyle: React.CSSProperties }) {
  const t = useTranslations("ClientKeys.Globals");
  const tQuoteCarousel = useTranslations("ClientKeys.BlockQuoteCarousel");
  const [activeIndex, setActiveIndex] = useState(0);
  const [key, setKey] = useState(0);
  const id = useId();

  if (!quotes || quotes.length === 0) {
    return null;
  }

  return (
    <div
      className='tw-relative tw-isolate tw-grid tw-grid-cols-1 tw-grid-rows-1'
      aria-roledescription='carousel'
      aria-label={label}
    >
      <div
        className='tw-relative tw-col-start-1 tw-row-start-1 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 tw-px-2 md:tw-px-4'
        inert
      >
        <div className='tw-relative tw-aspect-[2/1] sm:tw-aspect-square'>
          <div className='tw-absolute tw-inset-0 tw-isolate tw-rounded-2.5xl tw-overflow-hidden'>
            <AnimatePresence initial={false} mode='popLayout'>
              <m.div
                key={key}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1, transition: { duration: 0.3 } }}
                exit={{ opacity: 0, transition: { delay: 0.3 } }}
                className='tw-absolute tw-inset-0'
                style={{ zIndex: key }}
              >
                <QuoteAsset quote={quotes[activeIndex]} active={true} logoStyle={logoStyle} />
                {/* Preload previous and next slides */}
                <QuoteAsset
                  quote={quotes[(activeIndex - 1 + quotes.length) % quotes.length]}
                  logoStyle={logoStyle}
                  active={false}
                />
                <QuoteAsset quote={quotes[(activeIndex + 1) % quotes.length]} logoStyle={logoStyle} active={false} />
              </m.div>
            </AnimatePresence>
          </div>
          <HairlineCardBorder roundedClassName='tw-rounded-2.5xl' />
        </div>
      </div>
      <div id={`${id}-slides`} className='tw-contents'>
        {quotes.map((quote, quoteIndex) => (
          <div
            key={quoteIndex}
            className='tw-relative tw-col-start-1 tw-row-start-1 tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-4 tw-px-2 md:tw-px-4'
            style={{ zIndex: quoteIndex === activeIndex ? 1 : 0 }}
            inert={quoteIndex !== activeIndex}
            role='group'
            aria-roledescription='slide'
            aria-label={tQuoteCarousel("slide_label", { number: quoteIndex + 1, total: quotes.length })}
          >
            <div className='tw-relative tw-aspect-[2/1] sm:tw-aspect-square tw-rounded-2.5xl tw-transition-opacity'>
              <QuoteVideo quote={quote} />
            </div>
            <div className='sm:tw-col-span-2 tw-px-2 md:tw-pl-6 md:tw-pr-8 tw-flex tw-flex-col tw-justify-center'>
              <div className='tw-relative tw-flex-auto tw-grid tw-grid-cols-1 tw-grid-rows-1 xl:tw-py-9'>
                <figure
                  className={clsx(
                    "tw-col-start-1 tw-row-start-1 tw-flex tw-flex-col tw-justify-between tw-gap-10 tw-transition-opacity tw-duration-200",
                    quoteIndex === activeIndex ? "tw-delay-200" : "tw-opacity-0",
                  )}
                >
                  <blockquote>
                    <p className='tw-type-2xl lg:tw-type-3xl tw-text-black'>
                      <span className='tw-block tw-text-pretty tw-relative before:tw-content-[open-quote] before:tw-absolute before:tw-top-0 before:tw-right-full'>
                        <span className='after:tw-content-[close-quote]'>{quote.quote}</span>
                      </span>
                    </p>
                  </blockquote>
                  <figcaption className='tw-pr-24'>
                    <p className='tw-type-base tw-text-black'>{quote.author?.name}</p>
                    <p className='tw-mt-3 tw-type-xs tw-text-gray-500'>{quote.author?.role}</p>
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        ))}
      </div>
      {quotes.length > 1 && (
        <div className='tw-absolute tw-bottom-0 xl:tw-bottom-9 tw-right-4 md:tw-right-12 tw-flex tw-gap-3 tw-z-50'>
          <Button
            aria-label={t("previous_slide")}
            aria-controls={`${id}-slides`}
            icon={<ChevronLeft size={16} />}
            size='pill-md'
            variant='elevated'
            onClick={() => {
              setActiveIndex(a => (a - 1 + quotes.length) % quotes.length);
              setKey(k => k + 1);
            }}
          />
          <Button
            aria-label={t("next_slide")}
            aria-controls={`${id}-slides`}
            icon={<ChevronRight size={16} />}
            size='pill-md'
            variant='elevated'
            onClick={() => {
              setActiveIndex(a => (a + 1) % quotes.length);
              setKey(k => k + 1);
            }}
          />
        </div>
      )}
    </div>
  );
}
